import clsx from 'clsx';
import { useFormik } from 'formik';
import React from 'react';
import Button from '../../components/Button';
import { Link } from 'react-router';
import Input from '../../components/Input';
import { getForgotPasswordSchema } from './schema';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';

interface ForgotPasswordProps {
  otherClasses?: string;
}

const INITIAL_STATE = {
  email: '',
};

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ otherClasses }) => {
  const forgotPasswordClasses = clsx(
    otherClasses,
    'w-full max-w-325 lg:max-w-465 mx-auto flex flex-col items-center gap-10',
  );

  const { AuthApi } = useApiClients();
  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getForgotPasswordSchema(),
    onSubmit: async (values) => {
      try {
        const response = await AuthApi.authControllerResetPassword({
          resetPasswordDto: {
            email: values?.email,
          },
        });
        toast.success(response?.data?.message);
        resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const sizes = useButtonSizeLarge();

  return (
    <form
      action="#"
      onSubmit={handleSubmit}
      className={forgotPasswordClasses}
      data-testid="forgot-password"
      aria-labelledby="forgot-password-heading"
      noValidate
    >
      <h1 id="forgot-password-heading" className="sr-only">
        Forgot Password
      </h1>
      <div className="w-full">
        <p id="forgot-password-description" className="sr-only">
          Enter your email address below and we'll send you instructions to
          reset your password.
        </p>
        <Input
          onBlur={handleBlur}
          label="Email*"
          errorMessage={touched.email && errors.email ? errors.email : ''}
          disabled={isSubmitting}
          value={values.email}
          type="email"
          placeholder="someone@gmail.com"
          onChange={handleChange}
          name="email"
          otherClasses={{ container: 'w-full' }}
          aria-describedby={
            touched.email && errors.email
              ? 'email-error'
              : 'forgot-password-description'
          }
          aria-invalid={touched.email && errors.email ? true : false}
          aria-required={true}
        />
      </div>
      <Button
        size={sizes}
        otherClasses="w-full max-w-465"
        type="submit"
        aria-label="Send password reset email"
        disable={isSubmitting}
      >
        Send Email
      </Button>
      <div className="mt-7 flex flex-col items-center gap-5 text-center lg:mt-0">
        <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-medium md:text-lg lg:text-xl lg:leading-6">
          Go back to{' '}
          <Link
            to="/sign-in"
            className="text-brand-byzantine"
            aria-label="Return to sign in page"
          >
            Sign In
          </Link>
        </p>
      </div>
    </form>
  );
};

export default ForgotPassword;
